/**
 * 成功/中性提示的统一出口（文案 + 时长单源）。
 *
 * 背景：全站 300+ 处 `Message.success` 文案各写一套——同一动作在不同页面用词不同
 * （「保存成功」22 处 vs「已保存」18 处，甚至同一文件内并存），且默认 3 秒对"需要读一下"的
 * 提示（如"已保存，约 10 秒内生效"）不够读。这里收敛两件事：
 * 1. **文案**：同一动作同一句话（保存/已保存、删除/已删除、启用/停用…）；
 * 2. **时长**：确认类 3s、需阅读类 5s（`read()`），不再让页面随手写。
 *
 * 边界：只收「成功/中性」提示。**失败提示一律走 `request.ts` 拦截器**（见 frontend-dev-convention §14），
 * 页面禁止再弹 `Message.error`；需要自定义错误文案时用 `silent: true` 并在页面本地兜底。
 */
import { Message } from '@arco-design/web-react';

/** 确认类提示时长（一闪而过即可） */
export const MSG_DURATION = 3000;
/** 需阅读类提示时长（含生效时间、后续步骤等） */
export const MSG_DURATION_READ = 5000;

const withObj = (what?: string) => (what ? `${what} ` : '');

/** Arco 的 `Message.success` 只接一个配置对象（不接受 (content, duration) 两参），故统一走对象形态 */
const ok = (content: string, duration: number = MSG_DURATION) => Message.success({ content, duration });

export const msg = {
  /** 保存成功（含配置/表单/画布） */
  saved: (what?: string) => ok(`${withObj(what)}已保存`),
  /** 新建成功 */
  created: (what?: string) => ok(`${withObj(what)}已创建`),
  /** 删除成功（不可恢复的动作建议用 `done()` 写明后果） */
  deleted: (what?: string) => ok(`${withObj(what)}已删除`),
  /** 更新成功 */
  updated: (what?: string) => ok(`${withObj(what)}已更新`),
  /** 启停切换：统一「已启用 / 已停用」 */
  enabled: (on: boolean, what?: string) => ok(`${withObj(what)}${on ? '已启用' : '已停用'}`),
  /** 提交/发送成功 */
  submitted: (what?: string) => ok(`${withObj(what)}已提交`),
  /** 提交成功但需用户知道后续（如异步生效）：5 秒够读完一句 */
  pending: (text: string) => ok(text, MSG_DURATION_READ),
  /** 复制成功 */
  copied: () => ok('已复制'),
  /** 操作成功但带自定义文案（仅当上面几类都不贴切时使用） */
  done: (text: string, opts?: { read?: boolean }) => ok(text, opts?.read ? MSG_DURATION_READ : MSG_DURATION),
};
